import {
  HHDivider,
  HHStack,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrency } from '../../utils/currency-helpers';
import { CalculationStep, Operator } from './calculation-step';

export const LOCK_PRICE_CORRECTION_ICON_TEST_ID =
  'program-cap-lock-price-correction-icon';
export const LOCK_PRICE_SUMMARY_TEST_ID = 'program-cap-lock-price-section';

export interface LockPriceCorrectionProps {
  lockPrice: number;
  correctedUserBasedLockPrice: number;
}

const toCents = (amount: number): number => Math.round(amount * 100);

const getDifferenceInCents = ({
  lockPrice,
  correctedUserBasedLockPrice,
}: LockPriceCorrectionProps): number =>
  toCents(correctedUserBasedLockPrice) - toCents(lockPrice);

const formatDifference = (differenceInCents: number): string =>
  `${differenceInCents > 0 ? '+' : '-'}${formatCurrency(
    Math.abs(differenceInCents) / 100,
    'USD',
  )}`;

export const LockPriceCorrectionIcon = (
  props: LockPriceCorrectionProps,
): JSX.Element => {
  const differenceInCents = getDifferenceInCents(props);
  const title =
    differenceInCents === 0
      ? 'This lock price was calculated using the subscription-based member count. Recalculated by user, the lock price is the same.'
      : `Warning: this lock price was calculated using the subscription-based member count. Recalculated by user, the lock price is ${formatCurrency(
          props.correctedUserBasedLockPrice,
          'USD',
        )}, a difference of ${formatDifference(differenceInCents)}.`;

  return (
    <HHTooltip title={title} hhVariant="bottom">
      <InfoOutlinedIcon
        color="action"
        sx={{ fontSize: 16, display: 'block' }}
        data-testid={LOCK_PRICE_CORRECTION_ICON_TEST_ID}
      />
    </HHTooltip>
  );
};

export const LockPriceCorrectionSummary = (
  props: LockPriceCorrectionProps,
): JSX.Element => {
  const differenceInCents = getDifferenceInCents(props);

  return (
    <HHStack
      padding={4}
      paddingBottom={0}
      spacing={3}
      sx={{ backgroundColor: theme => theme.palette.background.default }}
      data-testid={LOCK_PRICE_SUMMARY_TEST_ID}
    >
      <HHTypography hhVariant="subtitle1">Lock Price</HHTypography>
      <HHDivider />
      <HHStack direction="row" alignItems="center">
        <CalculationStep
          value={formatCurrency(props.correctedUserBasedLockPrice, 'USD')}
          description="User-based"
        />
        <Operator symbol="-" />
        <CalculationStep
          value={formatCurrency(props.lockPrice, 'USD')}
          description="Subscription-based"
        />
        <Operator symbol="=" />
        <CalculationStep
          value={
            differenceInCents === 0
              ? 'No diff'
              : formatDifference(differenceInCents)
          }
          description="Difference"
        />
      </HHStack>
    </HHStack>
  );
};
